import { Injectable } from "@angular/core";

import { Subject } from "rxjs";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BfxBlotterAction,
  OrderSalesApprovalRequestPromiseService,
  OrderDataActionPromiseService,
  OrderAcceptancePromiseService,
  OrderDataLookupService,
} from "shared/cubiq-services";

import { TranslateService } from "@ngx-translate/core";

import { BfxInterventionContextMenuService } from "./bfx-intervention-contextmenu.service";
import { BfxInterventionBlotterComponent } from "../bfx-intervention-blotter.component";
import {
  GoldenLayoutService,
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
} from "../../golden-layout/golden-layout-service";
import { DashboardItemType } from "../../bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "../../golden-layout/golden-layout-addition-mode";
import { BfxInterventionBlotterService } from "./bfx-intervention-blotter.service";
import { BfxConfigService } from "../../../shared/auth-services/system-config/bfx-config.service";

// models
import { BfxDialogData } from "../../bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "../../bfx-dialogs/models/bfx-dialog-command.enum";
// base blotter imports
import {
  BfxBaseBlotterActionService,
  ActionListener,
} from "../../bfx-base-blotter/bfx-base-blotter-action.service";
import { BfxBlotterActionEvent } from "../../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { BfxOrderCancelComponentLauncher } from "../../bfx-order-cancel/bfx-order-cancel.launcher";
/// order -entry
import { ManualPriceActionPrimaryService } from "shared/cubiq-services";
import { OENOperationMode } from "app/order-entry/data/enums/oe-operation-mode.enum";
import { OELoadingResponseModel } from "app/order-entry/data/models/oe-loading-response.model";
import { OENLoadingOutcome } from "app/order-entry/data/enums/oe-loading-outcome.enum";
import { OEApiService } from "app/order-entry/services/oe-api.service";
import { OENEntryType } from "app/order-entry/data/enums/oe-entry-type.enum";
import { ManualPricingManagerService } from "../manual-pricing/services/manual-pricing-manager.service";
import {
  BfxInterventionContextMenuItemState,
  PortfolioPricingReference,
} from "../models/bfx-intervention-contextmenu.models";
import { LoggerService } from "@bfx/logger";
import { FwdRollPricingService } from "app/bfx-blotter-intervention/fwd-roll-pricing/services/fwd-roll-pricing.service";
const logger = LoggerService.for(__dirname);

@Injectable()
export class BfxInterventionActionsService extends BfxBaseBlotterActionService<any> {
  /**********************************************************************/
  /* OBSERVABLE */
  // Observable string sources
  private interventionActionDialogSource = new Subject<BfxDialogData>();
  // Observable string streams
  public dialogCtrl$ = this.interventionActionDialogSource.asObservable();

  // public actionListeners: any;
  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    private salesApprovalPromiseService: OrderSalesApprovalRequestPromiseService,
    private manualPriceService: ManualPriceActionPrimaryService,
    contextMenuService: BfxInterventionContextMenuService,
    layoutService: GoldenLayoutService,
    translationService: TranslateService,
    private ladderBlotterService: BfxInterventionBlotterService,
    configService: BfxConfigService,
    private _oeActions: OEApiService,
    private orderDataService: OrderDataLookupService,
    private orderDataActionService: OrderDataActionPromiseService,
    private orderAcceptanceService: OrderAcceptancePromiseService,
    private manualPricingManager: ManualPricingManagerService,
    private fwdRollPricingService: FwdRollPricingService,
  ) {
    super(contextMenuService, layoutService, translationService, configService);
  }

  @ActionListener(BfxBlotterAction.ACTION_DI_ACCEPT)
  public onAccept(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    if (itemState.traderReference instanceof Map && itemState.traderReference.size > 0) {
      itemState.traderReference.forEach((ids, acceptanceType) => {
        if (Array.isArray(ids) && ids.length > 0) {
          this.orderAcceptanceService.requestAcceptance(
            ids,
            "",
            CubiqTS.AcceptanceAction.ACCEPT,
            acceptanceType,
            null,
            null,
            CubiqTS.OrderType.BENCHMARK,
          );
        }
      });
    }
  }

  /* TRADER REJECT */
  @ActionListener(BfxBlotterAction.ACTION_DI_TRADER_REJECT)
  public onTraderReject(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    if (itemState.traderReference instanceof Map && itemState.traderReference.size > 0) {
      itemState.traderReference.forEach((ids, acceptanceType) => {
        if (Array.isArray(ids) && ids.length > 0) {
          this.orderAcceptanceService.requestAcceptance(
            ids,
            "",
            CubiqTS.AcceptanceAction.REJECT,
            acceptanceType,
            null,
            null,
            CubiqTS.OrderType.BENCHMARK,
          );
        }
      });
    }
  }

  /* APPROVE */
  @ActionListener(BfxBlotterAction.ACTION_DI_APPROVE)
  public onApprove(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    if (Array.isArray(itemState.referencedOrderIds) && itemState.referencedOrderIds.length > 0) {
      this.salesApprovalPromiseService.requestApproval(
        itemState.referencedOrderIds,
        "",
        CubiqTS.SalesApprovalAction.APPROVE,
      );
    }
  }

  /* SALES REJECT */
  @ActionListener(BfxBlotterAction.ACTION_DI_REJECT)
  public onReject(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    if (Array.isArray(itemState.referencedOrderIds) && itemState.referencedOrderIds.length > 0) {
      this.salesApprovalPromiseService.requestApproval(
        itemState.referencedOrderIds,
        "",
        CubiqTS.SalesApprovalAction.REJECT,
      );
    }
  }

  /* FILL_APPROVE */
  @ActionListener(BfxBlotterAction.ACTION_DI_FILL_APPROVE)
  public onFillApprove(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    if (Array.isArray(itemState.referencedOrderIds) && itemState.referencedOrderIds.length > 0) {
      this.salesApprovalPromiseService.requestApproval(
        itemState.referencedOrderIds,
        "",
        CubiqTS.SalesApprovalAction.APPROVE_FILL,
      );
    }
  }

  /* FILL_CANCEL */
  @ActionListener(BfxBlotterAction.ACTION_DI_FILL_CANCEL)
  public onFillCancel(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    if (Array.isArray(itemState.referencedOrderIds) && itemState.referencedOrderIds.length > 0) {
      this.salesApprovalPromiseService.requestApproval(
        itemState.referencedOrderIds,
        "",
        CubiqTS.SalesApprovalAction.CANCEL_FILL,
      );
    }
  }

  /* FILL_CLEAR */
  @ActionListener(BfxBlotterAction.ACTION_DI_FILL_CLEAR)
  public onFillClear(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    if (Array.isArray(itemState.referencedOrderIds) && itemState.referencedOrderIds.length > 0) {
      this.salesApprovalPromiseService.requestApproval(
        itemState.referencedOrderIds,
        "",
        CubiqTS.SalesApprovalAction.CLEAR_FILL,
      );
    }
  }

  @ActionListener(BfxBlotterAction.ACTION_DI_CANCEL)
  public onCancel(event: BfxBlotterActionEvent): void {
    // TODO not sure what these do/did? (unref in BfxOrderCancelComponent -dc)
    event["breach"] = false;
    event["transferItems"] = [];

    BfxOrderCancelComponentLauncher.launch(
      event.itemState.referencedOrderData,
      this.layoutService,
      this.translationService,
      event,
    );
  }

  @ActionListener(BfxBlotterAction.ACTION_DI_AMEND_MARKUP)
  public onAmendMarkup(event: BfxBlotterActionEvent) {
    logger.out.info("DEV-TEST: onAmendMarkup() event: ", event);
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    let orderRef =
      event.selectedItems[0] != null && event.selectedItems[0].orderData != null
        ? event.selectedItems[0].orderData.orderRef
        : null;
    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = false;
    config.preferredSize = { width: "auto", height: "auto" };
    config.minimumSize = { width: 530, height: 500 };

    config.data = {};
    config.data.loadDefaults = false;
    config.data.mode = OENOperationMode.AMEND_MARKUP;
    config.data.entryType = OENEntryType.SPOT;
    config.ladderBlotter = event.ladderBlotter;

    config.data.title =
      <string>this.translationService.instant("order_entry.amend_markup.title") +
      (orderRef != null ? " - " + orderRef : "");

    this._oeActions
      .loadOrderEntryModelForOrderId(event.selectedIds[0], OENOperationMode.AMEND_MARKUP)
      .then(
        (response: OELoadingResponseModel) => {
          if (response != null && response instanceof OELoadingResponseModel) {
            switch (response.outcome) {
              case OENLoadingOutcome.WARNING_CONTINUE:
                logger.out.warn(
                  `WARN & CONTINUE: Order Entry ${OENOperationMode[config.data.mode]} LOAD: %O`,
                  response.message,
                );
              // no break here
              case OENLoadingOutcome.SUCCESS:
                if (response.refId && response.rootRefId && response.entryType != null) {
                  config.data.refId = response.refId;
                  config.data.rootRefId = response.rootRefId;
                  config.data.entryType = response.entryType;
                  this.layoutService.launchFromDashboard(
                    config.data.title,
                    DashboardItemType.OrderAmendMarkup,
                    true,
                    GoldenLayoutAdditionMode.DEFAULT,
                    config,
                  );
                } else {
                  logger.out.error(
                    `ERROR: Order Entry ${
                      OENOperationMode[config.data.mode]
                    } LOAD: Invalid IDs in response => refId: ${response.refId} | rootRefId: ${
                      response.rootRefId
                    }`,
                  );
                }
                break;
              case OENLoadingOutcome.WARNING_BREAK:
                logger.out.warn(
                  `WARN & EXIT: Order Entry ${OENOperationMode[config.data.mode]} LOAD:  %O`,
                  response.message,
                );
                break;
              case OENLoadingOutcome.ERROR:
                logger.out.error(
                  `ERROR: Order Entry ${OENOperationMode[config.data.mode]} LOAD:  %O`,
                  response.message,
                );
                break;
              default:
                logger.out.error(
                  `ERROR: Order Entry ${
                    OENOperationMode[config.data.mode]
                  } LOAD: Invalid outcome in response:  %O`,
                  response,
                );
                break;
            }
          } else {
            logger.out.error(
              `ERROR: Order Entry ${
                OENOperationMode[config.data.mode]
              } LOAD: Invalid response:  %O`,
              response,
            );
          }
        },
        (error) => {
          logger.out.error(
            `ERROR: Order Entry ${OENOperationMode[config.data.mode]} LOAD FAIL! Error:  %O`,
            error,
          );
        },
      );
  }

  @ActionListener(BfxBlotterAction.ACTION_DI_MANUAL_PRICE)
  public onPrice(event: BfxBlotterActionEvent) {
    let selectedIds: Array<string> = event.itemState.referencedOrderIds;
    if (Array.isArray(selectedIds) && selectedIds.length > 0) {
      this.manualPriceService.issueManualPriceLock(selectedIds).then(
        (response) => {
          if (
            response instanceof CubiqTS.StandardReportImpl &&
            Array.isArray(response.results) &&
            response.results.length > 0
          ) {
            this.manualPricingManager.manualPrice(response.results);
          }
        },
        (error) => {
          logger.out.error(`ERROR: Manual Price: Invalid lock response: `, error);
        },
      );
    }
  }

  @ActionListener(BfxBlotterAction.ACTION_DI_FWD_ROLL_PRICE)
  public onFwdRollPrice(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    let selectedReferences = itemState.portfolioReferences;
    if (Array.isArray(selectedReferences) && selectedReferences.length == 1) {
      this.fwdRollPricingService.fwdRollPrice(
        selectedReferences[0].diEventId,
        selectedReferences[0].portfolioId,
      );
    }
  }

  @ActionListener(BfxBlotterAction.ACTION_DI_FWD_ROLL_CLEAR)
  public onFwdRollClear(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    let selectedItems: Array<any> = itemState.portfolioReferences;
    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      let portfolioIds: string[] = selectedItems.map((p) => p.portfolioId);
      this.fwdRollPricingService.clear(portfolioIds).then(
        (response) => {
          // do nothing
        },
        (error) => {
          // show notification
          logger.out.error(`ERROR: Manual Price: Invalid lock response: `, error);
        },
      );
    }
  }

  @ActionListener(BfxBlotterAction.ACTION_DI_FWD_ROLL_RETRY)
  public onFwdRollRetry(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    let selectedItems: Array<any> = itemState.portfolioReferences;
    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      let portfolioIds: string[] = selectedItems.map((p) => p.portfolioId);
      this.fwdRollPricingService.retry(portfolioIds).then(
        (response) => {
          // do nothing
        },
        (error) => {
          // show notification
          logger.out.error(`ERROR: Retry: Invalid retry response: `, error);
        },
      );
    }
  }

  @ActionListener(BfxBlotterAction.ACTION_DI_UNLOCK)
  public onUnlock(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BfxInterventionContextMenuItemState;
    let selectedIds: Array<string> = itemState.referencedOrderIds;
    if (Array.isArray(selectedIds) && selectedIds.length > 0) {
      this.orderDataActionService.releaseOrder(selectedIds, null);
    }
    let unlockPricingIds: Array<string> = itemState.pricingReference;
    if (Array.isArray(unlockPricingIds) && unlockPricingIds.length > 0) {
      this.manualPriceService.issueManualPriceUnlock(unlockPricingIds);
    }
    let portfolioIds: Array<PortfolioPricingReference> = itemState.portfolioReferences;
    if (Array.isArray(portfolioIds) && portfolioIds.length > 0) {
      this.fwdRollPricingService.unlockPortfolios(portfolioIds);
    }
  }

  @ActionListener(BfxBlotterAction.ACTION_DI_VIEW_DETAILS)
  public onViewDetails(event: BfxBlotterActionEvent) {
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = false;
    config.sourceEvent = event.sourceEvent;

    config.data = { openTradesTab: false };

    // don't persist selectedIds in config
    Object.defineProperty(config.data, "selectedIds", {
      enumerable: false,
      writable: true,
    });

    config.data.selectedIds = event.itemState.referencedOrderIds;
    config.ladderBlotter = event.ladderBlotter;
    config.data.selectedDIEventIds = event.selectedIds;
    let orderRef =
      event.selectedItems[0] != null && event.selectedItems[0].orderData != null
        ? event.selectedItems[0].orderData.orderRef
        : null;
    let title: string =
      orderRef == null
        ? this.translationService.instant("order.details.title.noid")
        : this.translationService.instant("order.details.title", [orderRef]);

    this.layoutService.launchFromDashboard(
      title,
      DashboardItemType.OrderDetails,
      true,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  /*  @ActionListener(BfxBlotterAction.ACTION_EXPORT)
    public onExport(event: BfxBlotterActionEvent) {
        this.interventionActionDialogSource.next( new BfxDialogData(BfxDialogCommand.DIALOG_CSV_EXPORT_OPEN, null, event,  {blotterName: 'InterventionBlotter'}));
    }

 */
}
